<template>
	<div>
		<j-card class="bg5">
			<gross-premium-amount
				:id="computedId"
				:pricing="pricing"
				:isLoading="isLoading"
				:hasPremiumActions="hasPremiumActions"
				:onUpdatePremium="updateOfferPremium"
			/>

			<!-- premium-loadings -->
			<premium-loadings
				:id="computedId"
				:productId="productId"
				:pricing="pricing"
				:hasLoadingsActions="hasLoadingsActions"
				:addLoading="addLoadingToProposalOffer"
				:removeLoading="removeLoadingFromProposalOffer"
			/>

			<!-- premium-discounts -->
			<premium-discounts
				:id="computedId"
				:pricing="pricing"
				:hasDiscountLogic="hasDiscountLogic"
				:hasDiscountActions="hasDiscountActions"
				:addDiscountWithCode="addDiscountWithCode"
				:addDiscountWithoutCode="addDiscountWithoutCode"
				:removeDiscount="removeDiscountFromOffer"
				:nameOptions="discountNameOptions"
			/>

			<!-- price breakdown -->
			<premium-breakdown :pricing="pricing" />

			<!-- net premium amount -->
			<net-premium-amount
				:id="computedId"
				:pricing="pricing"
				:isLoading="isLoading"
				:hasPremiumActions="hasPremiumActions"
				:onUpdatePremium="updateOfferPremium"
				@premium:reset="updateOfferPremium({ type: 'net', amount: null })"
			/>
		</j-card>
	</div>
</template>

<script>
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

import JCard from "@/components/JCard"
import GrossPremiumAmount from "./GrossPremiumAmount"
import PremiumLoadings from "./PremiumLoadings"
import PremiumDiscounts from "./PremiumDiscounts"
import PremiumBreakdown from "./PremiumBreakdown"
import NetPremiumAmount from "./NetPremiumAmount"

export default {
	components: {
		JCard,
		GrossPremiumAmount,
		PremiumLoadings,
		PremiumDiscounts,
		PremiumBreakdown,
		NetPremiumAmount
	},

	props: {
		caseId: {
			type: String
		},
		proposalId: {
			type: String
		},
		offerId: {
			type: String
		},
		productId: {
			type: Object
		},
		pricing: {
			type: Object,
			required: true,
			default: () => ({
				amount: null,
				currencyCode: null,
				formattedPrice: null,
				originalPrice: null,
				formattedOriginalPrice: null,
				appliedTaxes: {
					code: null,
					ratio: null,
					name: null,
					order: null,
					flat: null,
					formattedFlat: null,
					formattedOriginalPrice: null
				},
				appliedDiscounts: {
					id: null,
					code: null,
					ratio: null,
					flat: null,
					formattedFlat: null,
					order: null,
					formattedOriginalPrice: null
				},
				loadings: {
					id: null,
					codeName: {
						code: null,
						name: null
					},
					ratio: null,
					flat: null
				}
			})
		},
		hasLoadingsActions: {
			type: Boolean,
			required: false,
			default: false
		},

		hasDiscountActions: {
			type: Boolean,
			required: false,
			default: false
		},

		hasDiscountLogic: {
			type: Boolean,
			required: false,
			default: false
		},

		hasPremiumActions: {
			type: Boolean,
			required: false,
			default: false
		},

		discountNameOptions: {
			type: Array,
			default: () => [],
			example: [{ name: "Discount name", value: "Discount Value" }]
		}
	},

	computed: {
		computedId() {
			return this.offerId
		}
	},

	data() {
		return {
			isLoading: false
		}
	},

	methods: {
		async updateOfferPremium({ type, amount }) {
			const query = gql`
				mutation updateOfferOfProposal(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$updateOfferInput: updateOfferInput!
				) {
					updateOfferOfProposal(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						updateOfferInput: $updateOfferInput
					) {
						errors
						errors_2 {
							code
							message
						}
						status
					}
				}
			`

			const premium = {
				currencyCode: this.pricing.currencyCode // we have to save currency code as well otherwise things will break
			}

			if (type === "gross") {
				premium.grossAmount = amount

				// TODO: shy is it updating discounts here?
				premium.discountCodes = this.pricing?.appliedDiscounts?.map(
					discount => discount.code
				)
			} else if (type === "net") {
				premium.amount = amount
			}

			const variables = {
				caseId: this.caseId,
				proposalId: this.proposalId,
				offerId: this.offerId,
				updateOfferInput: {
					premium
				}
			}

			this.isLoading = true
			const res = await api({ query, variables })

			const data = res?.data?.updateOfferOfProposal
			if (data?.status !== "success") {
				this.$store.dispatch(
					"showError",
					data?.errors_2?.map(item => item.message) || [
						this.$t("errors.general")
					]
				)
				this.isLoading = false
				return
			}

			this.$emit("premium:updated")
			setTimeout(() => {
				this.$modal.hide(`edit_${type}_premium_${this.computedId}`)
				this.isLoading = false
			}, 500)
		},

		async addLoadingToProposalOffer({ code, amount, type, order }) {
			const query = gql`
				mutation addLoadingToProposalOffer(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$input: loadingInput!
				) {
					addLoadingToProposalOffer(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						input: $input
					) {
						errors
						status
					}
				}
			`
			const variables = {
				caseId: this.caseId,
				proposalId: this.proposalId,
				offerId: this.offerId,
				input: {
					code,
					[type]: type === "ratio" ? amount / 100 : amount,
					order
				}
			}

			const res = await api({ query, variables })

			// Errors
			if (res?.data?.addLoadingToProposalOffer?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.addLoadingToProposalOffer?.errors ||
						this.$t("errors.general")
				)
				return Promise.resolve(res)
			}

			// Success
			setTimeout(() => {
				this.$modal.hide(`addLoading_${this.computedId}`)
			}, 500)

			this.$emit("loadings:added")
			return Promise.resolve(res)
		},

		async removeLoadingFromProposalOffer(id) {
			const query = gql`
				mutation removeLoadingFromProposalOffer(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$loadingId: String!
				) {
					removeLoadingFromProposalOffer(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						loadingId: $loadingId
					) {
						errors
						status
					}
				}
			`
			const variables = {
				caseId: this.caseId,
				proposalId: this.proposalId,
				offerId: this.offerId,
				loadingId: id
			}

			this.isLoading = true
			const res = await api({ query, variables })
			this.isLoading = false

			if (res?.data?.removeLoadingFromProposalOffer?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.removeLoadingFromProposalOffer?.errors ||
						this.$t("errors.general")
				)
				return
			}

			this.$emit("loadings:deleted")
		},

		async removeDiscountFromOffer(discount) {
			// Based on whether the discount has a code or not, we call particular API
			if (discount.code) this.removeDiscountWithCode(discount)
			else this.removeDiscountWithoutCode(discount)
		},

		async removeDiscountWithoutCode(discount) {
			const query = gql`
				mutation removeDiscountFromProposalOffer(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$discountId: String!
				) {
					removeDiscountFromProposalOffer(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						discountId: $discountId
					) {
						errors
						errors_2 {
							code
							message
						}
						status
					}
				}
			`
			const variables = {
				caseId: this.caseId,
				proposalId: this.proposalId,
				offerId: this.offerId,
				discountId: discount.id
			}

			const res = await api({ query, variables })
			const data = res?.data?.removeDiscountFromProposalOffer

			if (data?.status !== "success") {
				this.$store.dispatch(
					"showError",
					data?.errors_2?.map(item => item.message) || [
						this.$t("errors.general")
					]
				)
				return Promise.resolve(res)
			}

			this.$emit("discount:deleted")
			return Promise.resolve(res)
		},

		async removeDiscountWithCode(discount) {
			const query = gql`
				mutation updateOfferOfProposal(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$updateOfferInput: updateOfferInput!
				) {
					updateOfferOfProposal(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						updateOfferInput: $updateOfferInput
					) {
						errors
						errors_2 {
							code
							message
						}
						status
					}
				}
			`
			const variables = {
				caseId: this.caseId,
				proposalId: this.proposalId,
				offerId: this.offerId,
				updateOfferInput: {
					productId: this.productId,
					premium: {
						discountCodes: this.pricing.appliedDiscounts
							.filter(item => item.id !== discount.id)
							.map(item => item.code)
					}
				}
			}

			this.isLoading = true
			const res = await api({ query, variables })
			this.isLoading = false

			const data = res?.data?.updateOfferOfProposal

			if (data?.status !== "success") {
				this.$store.dispatch(
					"showError",
					data?.errors_2?.map(item => item.message) || [
						this.$t("errors.general")
					]
				)
				return
			}

			this.$emit("discount:deleted")
		},

		async addDiscountWithoutCode({ calculationJsonLogic, order, name }) {
			const query = gql`
				mutation addDiscountToProposalOffer(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$input: addDiscountToOfferInput!
				) {
					addDiscountToProposalOffer(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						input: $input
					) {
						status
						errors
					}
				}
			`

			this.isLoading = true
			const res = await api({
				query,
				variables: {
					caseId: this.caseId,
					proposalId: this.proposalId,
					offerId: this.offerId,
					input: {
						calculationJsonLogic,
						order,
						name
					}
				}
			})

			setTimeout(() => {
				this.isLoading = false
				this.$modal.hide(`addDiscount_${this.computedId}`)
			}, 500)

			// Errors
			const data = res?.data?.addDiscountToProposalOffer

			if (data?.status !== "success") {
				this.$store.dispatch(
					"showError",
					data?.errors_2?.map(item => item.message) || [
						this.$t("errors.general")
					]
				)
				return
			}

			// Emit
			this.$emit("discount:added")
		},

		async addDiscountWithCode(discountCode) {
			// Add discount code
			const addDiscountQuery = gql`
				mutation updateOfferOfProposal(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$updateOfferInput: updateOfferInput!
				) {
					updateOfferOfProposal(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						updateOfferInput: $updateOfferInput
					) {
						errors
						errors_2 {
							code
							message
						}
						status
					}
				}
			`

			const checkOfferQuery = gql`
				query checkDiscounts($where: policyWhereInput) {
					policies(where: $where) {
						list {
							offers {
								id
								premium {
									appliedDiscounts {
										code
									}
								}
							}
						}
					}
				}
			`

			const variables = {
				caseId: this.caseId,
				proposalId: this.proposalId,
				offerId: this.offerId,
				updateOfferInput: {
					premium: {
						discountCodes: this.pricing.appliedDiscounts
							.map(discount => discount.code)
							.concat(discountCode)
					}
				}
			}

			// Call apis
			this.isLoading = true
			const res = await api({ query: addDiscountQuery, variables })
			const quote = await api({
				query: checkOfferQuery,
				variables: {
					where: { id: this.policyId }
				}
			})

			setTimeout(() => {
				this.$modal.hide(`addDiscount_${this.computedId}`)
				this.isLoading = false
			}, 500)

			// Check errors
			if (
				res?.data?.updateOfferOfProposal?.status !== "success" ||
				quote?.errors
			) {
				this.$store.dispatch("showError", [this.$t("errors.general")])
				return
			}

			// Get appliedDiscounts and figure out if discount was applied
			const appliedDiscounts = quote.data?.policies?.list
				?.find(policy => policy.offers.find(offer => offer.id === this.offerId))
				?.offers?.find(offer => offer.id === this.offerId)?.premium
				?.appliedDiscounts

			// if applied discounts is not found, show error
			if (!appliedDiscounts?.some(discount => discount.code === discountCode)) {
				this.$store.dispatch("showError", [
					`The discount code ${discountCode} is invalid and was not applied`
				])
				return
			}

			// Emit
			this.$emit("discount:added")
		}
	}
}
</script>

<style></style>
